<script setup lang="ts">
import { TransitionPresets } from '@vueuse/core'

type TransitionsPresetsKeys = keyof typeof TransitionPresets

interface NumberTickerProps {
  decimalPlaces?: number
  delay?: number
  direction?: 'down' | 'up'
  duration?: number
  transition?: TransitionsPresetsKeys
  value: number | Ref<number>
}

const props = withDefaults(defineProps<NumberTickerProps>(), {
  decimalPlaces: 0,
  delay: 0,
  direction: 'up',
  duration: 1000,
  transition: 'easeOutCubic',
  value: 0,
})

const spanRef = ref<HTMLSpanElement>()

const transitionValue = ref(props.direction === 'down' ? props.value : 0)

const isAnimating = ref(false)

const transitionOutput = useTransition(
  transitionValue,
  {
    delay: props.delay,
    duration: props.duration,
    onFinished: () => isAnimating.value = false,
    onStarted: () => isAnimating.value = true,
    transition: TransitionPresets[props.transition],
  },
)

const output = computed(() => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: props.decimalPlaces,
    minimumFractionDigits: props.decimalPlaces,
  }).format(Number(transitionOutput.value.toFixed(props.decimalPlaces)))
})

const isInView = useElementVisibility(spanRef, { threshold: 0 })

watch(
  isInView,
  (isVisible) => {
    if (isVisible) {
      transitionValue.value = props.direction === 'down' ? 0 : props.value
    }
  },
  { immediate: true },
)

watch(
  () => props.value,
  (newValue) => {
    transitionValue.value = props.direction === 'down' ? 0 : newValue
  },
)

defineExpose({ isAnimating })
</script>

<template>
  <span
    ref="spanRef"
    class="inline-block tabular-nums"
  >
    {{ output }}
  </span>
</template>
